import React, { useEffect, useState, useContext } from "react"
import { AppContext } from "../utilities/context";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import axios from "axios";
import Container from "@mui/material/Container";
import Layout from "./Layout"; // Considera usar un LayoutAdmin/Kitchen
import {
  Typography,
  Grid,
  CircularProgress,
  Box,
  Card,
  CardContent,
  Divider,
  Button,
  List,
  ListItem,
  ListItemText,
  Chip,
  Alert,
  Snackbar,
} from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";

const formatOrderDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleString();
};

const OrdersByStatus = () => {
  const intl = useIntl();
  const context = useContext(AppContext);
  
  // No necesitamos `currentShopId` del contexto para las llamadas a la API,
  // pero podríamos usarlo para inicializar el componente o si es necesario para otros fines de UI.
  // Por ahora, lo removemos para enfatizar que el backend lo gestiona.
  // const { currentShopId } = useContext(AppContext);

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentStatusFilter, setCurrentStatusFilter] = useState("PENDING");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const timeout = 5000; // Intervalo de auto-refresh en ms
  const [autoRefresh, setAutoRefresh] = useState(false);

  useEffect(() => {
    if (context) {
      context.updateBreadcrumbsState([
        {
          label: intl.formatMessage({ id: "app.header.home.label" }),
          url: "/",
        },
        {
          label: intl.formatMessage({ id: "app.header.statuses.label" }),
        },
      ]);
    }
    fetchOrdersByStatus(currentStatusFilter);
  }, [currentStatusFilter]); // Solo dependemos del filtro de estado

  const fetchOrdersByStatus = async (status) => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/orders/status/${status}`);
      setOrders(response.data);
    } catch (err) {
      console.error(`Error fetching ${status} orders:`, err);
      // Podría haber un error 403 (Unauthorized) si el token no es válido o no tiene permisos
      setSnackbar({
        open: true,
        message: intl.formatMessage({
          id: "app.errorLoadingOrders",
          defaultMessage:
            "Error al cargar los pedidos. (¿Sesión expirada o sin permisos?)",
        }),
        severity: "error",
      });
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(
      () => fetchOrdersByStatus(currentStatusFilter),
      timeout
    );
    return () => clearInterval(interval);
  }, [autoRefresh, currentStatusFilter, timeout]);

  const handleUpdateOrderStatus = async (orderId) => {
    try {
      await axios.put(`/api/orders/${orderId}/status`);
      setSnackbar({
        open: true,
        message: intl.formatMessage({
          id: "app.orderStatusUpdated",
          defaultMessage: "Estado del pedido actualizado!",
        }),
        severity: "success",
      });
      fetchOrdersByStatus(currentStatusFilter);
    } catch (err) {
      console.error(
        `Error updating status for order ${orderId}:`,
        err.response?.data?.message || err.message
      );
      setSnackbar({
        open: true,
        message: intl.formatMessage(
          {
            id: "app.errorUpdatingStatus",
            defaultMessage: "Error al actualizar el estado del pedido: {error}",
          },
          { error: err.response?.data?.message || err.message }
        ),
        severity: "error",
      });
    }
  };

  const renderOrderCard = (order) => (
    <Grid item xs={12} sm={6} md={4} key={order.id}>
      <Card
        variant="outlined"
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          bgcolor: "#fffde7",
        }}
      >
        <CardContent sx={{ flexGrow: 1 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={1}
          >
            <Typography variant="h6" component="div">
              <FormattedMessage
                id="app.orderNumberShort"
                defaultMessage="Pedido #"
              />
              {order.orderNumber}
            </Typography>
            <Chip
              label={order.order_status?.name || order.status}
              color={
                order.status === "PENDING"
                  ? "warning"
                  : order.status === "PREPARING"
                  ? "info"
                  : order.status === "READY"
                  ? "success"
                  : "default"
              }
              size="small"
            />
          </Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            <FormattedMessage
              id="app.orderTime"
              defaultMessage="Hora: {time}"
              values={{ time: formatOrderDate(order.createdAt) }}
            />
          </Typography>
          <Typography variant="body1" gutterBottom>
            <strong>
              <FormattedMessage id="app.table" defaultMessage="Mesa:" />
            </strong>{" "}
            {order.location?.name || "N/A"}
          </Typography>
          {order.notes && (
            <Typography variant="body2" color="text.secondary">
              <strong>
                <FormattedMessage
                  id="app.generalNotes"
                  defaultMessage="Notas:"
                />
              </strong>{" "}
              {order.notes}
            </Typography>
          )}

          <Divider sx={{ my: 1 }} />

          <Typography variant="subtitle1" fontWeight="bold">
            <FormattedMessage id="app.items" defaultMessage="Items:" />
          </Typography>
          <List dense>
            {order.products &&
              order.products.map((item) => (
                <ListItem key={item.id} disableGutters>
                  <ListItemText
                    primary={`${item.order_products.quantity}x ${item.name}`}
                    secondary={
                      item.order_products.productNotes
                        ? `(${item.order_products.productNotes})`
                        : null
                    }
                  />
                </ListItem>
              ))}
          </List>

          <Divider sx={{ my: 1 }} />

          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6">
              <FormattedMessage id="app.total" defaultMessage="Total:" />
            </Typography>
            <Typography variant="h6" fontWeight="bold">
              ${parseFloat(order.total).toFixed(2)}
            </Typography>
          </Box>
        </CardContent>
        <Box sx={{ p: 2, borderTop: "1px solid #eee" }}>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={() => handleUpdateOrderStatus(order.id)}
            disabled={loading || order.order_status?.isFinal}
          >
            {intl.formatMessage({
              id: "app.nextStage",
              defaultMessage: "Siguiente Etapa",
            })}
          </Button>
        </Box>
      </Card>
    </Grid>
  );

  return (
    <Layout>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ mb: 3 }}>
          <FormControlLabel
            control={
              <Switch
                checked={autoRefresh}
                onChange={(_, checked) => setAutoRefresh(checked)}
                color="primary"
              />
            }
            label="Auto Refresh"
            sx={{ mr: 2 }}
          />
          <Button
            variant={
              currentStatusFilter === "PENDING" ? "contained" : "outlined"
            }
            onClick={() => setCurrentStatusFilter("PENDING")}
            sx={{ mr: 1 }}
          >
            <FormattedMessage id="app.pending" defaultMessage="Pendientes" />
          </Button>
          <Button
            variant={
              currentStatusFilter === "PREPARING" ? "contained" : "outlined"
            }
            onClick={() => setCurrentStatusFilter("PREPARING")}
            sx={{ mr: 1 }}
          >
            <FormattedMessage
              id="app.preparing"
              defaultMessage="En Preparación"
            />
          </Button>
          <Button
            variant={currentStatusFilter === "READY" ? "contained" : "outlined"}
            onClick={() => setCurrentStatusFilter("READY")}
          >
            <FormattedMessage id="app.ready" defaultMessage="Listos" />
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
            <CircularProgress />
            <Typography variant="h6" sx={{ ml: 2 }}>
              <FormattedMessage
                id="app.loadingOrders"
                defaultMessage="Cargando pedidos..."
              />
            </Typography>
          </Box>
        ) : orders.length === 0 ? (
          <Alert severity="info" sx={{ mt: 4 }}>
            <FormattedMessage
              id="app.noOrdersInStatus"
              defaultMessage="No hay pedidos en estado '{status}'."
              values={{
                status: intl.formatMessage({
                  id: `app.${currentStatusFilter.toLowerCase()}`,
                  defaultMessage: currentStatusFilter,
                }),
              }}
            />
          </Alert>
        ) : (
          <Grid container spacing={3}>
            {orders.map(renderOrderCard)}
          </Grid>
        )}

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Layout>
  );
};

export default OrdersByStatus;
